// ---------------------------------------------------------------------------
// Lectura llana del detalle de una hora (#537). Lógica pura, fuera de los .tsx,
// para probarse sin montar componentes (ni tocar `utils/api` → Firebase).
//
// Regla de oro: NADA se inventa. Todo lo que devuelven estas funciones sale del
// dato que mandó la API; si un commit no trae mensaje, se devuelve `null` y la
// UI lo dice con todas sus letras en vez de rellenar el hueco.
// ---------------------------------------------------------------------------
import type { PullRequestRef } from "@torre/work-hours";

/** Commit tal como viaja dentro de una franja (`HourEntry.commits`). */
export interface CommitLike {
  sha: string;
  repos?: string[];
  branches?: string[];
  message?: string | null;
  url?: string | null;
  authoredAt?: string;
  pullRequests?: PullRequestRef[];
}

/** Franja tal como la devuelve `workHoursEntries`. */
export interface EntryLike {
  userId: string;
  userName?: string | null;
  repos?: string[];
  commits?: CommitLike[];
}

/**
 * Prefijos de Conventional Commits que se degradan a etiqueta: son metadatos
 * del proceso, no la explicación del trabajo. La lista es cerrada a propósito —
 * un mensaje como «Corrige: el cálculo» NO es un prefijo técnico y se respeta
 * tal cual.
 */
export const COMMIT_TYPES = new Set([
  "build",
  "chore",
  "ci",
  "docs",
  "feat",
  "fix",
  "hotfix",
  "perf",
  "refactor",
  "revert",
  "style",
  "test",
  "tests",
  "wip",
]);

export interface PlainMessage {
  /** Primera línea en lenguaje llano, o `null` si el commit no trae mensaje. */
  text: string | null;
  /** Prefijo técnico (`feat`, `fix`…) si lo había, para pintarlo como etiqueta secundaria. */
  type: string | null;
  /** Ámbito entre paréntesis (`feat(api):` → `api`), si venía. */
  scope: string | null;
}

const capitalize = (s: string): string => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

/**
 * Convierte el mensaje de un commit en algo legible: se queda con la primera
 * línea y saca del protagonismo el prefijo `tipo(ámbito):`.
 */
export function plainCommitMessage(message?: string | null): PlainMessage {
  const first = String(message ?? "").split("\n")[0].trim();
  if (!first) return { text: null, type: null, scope: null };

  const m = /^([A-Za-z]+)(?:\(([^()]*)\))?!?:\s*(.*)$/.exec(first);
  if (!m) return { text: first, type: null, scope: null };

  const type = m[1].toLowerCase();
  const rest = (m[3] ?? "").trim();
  // Sin texto tras el prefijo no hay nada que explicar: se deja el mensaje
  // original antes que dejar la celda vacía.
  if (!COMMIT_TYPES.has(type) || !rest) return { text: first, type: null, scope: null };
  return { text: capitalize(rest), type, scope: m[2]?.trim() || null };
}

/** Clave estable de un PR (`owner/repo#123`). */
export const prKey = (pr: { repo: string; number: number }): string => `${pr.repo}#${pr.number}`;

export interface HourDetailDigest {
  /** SHAs distintos: el mismo commit en dos forks se cuenta una vez. */
  commits: number;
  repos: string[];
  pullRequests: PullRequestRef[];
  /** Nombre (o id, si no hay nombre) de quienes trabajaron esa hora. */
  people: string[];
}

/** Resumen corto de una hora: cuántos commits, en qué repos, qué PRs y quiénes. */
export function summarizeHourDetail(entries: EntryLike[] | null | undefined): HourDetailDigest {
  const shas = new Set<string>();
  const repos = new Set<string>();
  const prs = new Map<string, PullRequestRef>();
  const people = new Set<string>();

  for (const entry of entries ?? []) {
    const person = entry.userName ?? entry.userId;
    if (person) people.add(person);
    for (const r of entry.repos ?? []) repos.add(r);
    for (const commit of entry.commits ?? []) {
      if (commit.sha) shas.add(commit.sha);
      for (const r of commit.repos ?? []) repos.add(r);
      for (const pr of commit.pullRequests ?? []) if (pr && pr.repo) prs.set(prKey(pr), pr);
    }
  }

  return {
    commits: shas.size,
    repos: [...repos].sort(),
    pullRequests: [...prs.values()].sort((a, b) => prKey(a).localeCompare(prKey(b))),
    people: [...people].sort(),
  };
}

export interface GroupedCommit extends CommitLike {
  /** Quién firmó la franja donde apareció el commit. */
  person: string | null;
  message: string | null;
}

export interface RepoCommitGroup {
  /** `owner/repo`, o `null` si el commit llegó sin repositorio. */
  repo: string | null;
  commits: GroupedCommit[];
}

/**
 * Agrupa por repositorio los commits de las franjas, sin duplicar SHAs. Un SHA
 * que vive en varios repos (forks de la misma familia) aparece en cada uno: es
 * lo que dice el dato, y el conteo de `summarizeHourDetail` no se infla porque
 * cuenta SHAs distintos.
 */
export function groupCommitsByRepo(entries: EntryLike[] | null | undefined): RepoCommitGroup[] {
  const seen = new Set<string>();
  const groups = new Map<string, GroupedCommit[]>();

  for (const entry of entries ?? []) {
    const person = entry.userName ?? entry.userId ?? null;
    for (const commit of entry.commits ?? []) {
      if (commit.sha && seen.has(commit.sha)) continue;
      if (commit.sha) seen.add(commit.sha);
      const item: GroupedCommit = { ...commit, message: commit.message ?? null, person };
      const keys = commit.repos && commit.repos.length ? commit.repos : [""];
      for (const key of keys) {
        const list = groups.get(key);
        if (list) list.push(item);
        else groups.set(key, [item]);
      }
    }
  }

  return [...groups.entries()]
    .map(([repo, commits]) => ({ repo: repo || null, commits }))
    .sort((a, b) => (a.repo === null ? 1 : b.repo === null ? -1 : a.repo.localeCompare(b.repo)));
}
